//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Map Layers tab
//
//  Copyright:    © 2021 - 2025 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001            Sean Flook                  Initial Revision.
//    002   27.06.23 Sean Flook          WI40757 Added in deletion confirmation dialog.
//    003   07.09.23 Sean Flook                  Removed unnecessary awaits.
//    004   06.10.23 Sean Flook                  Use colour variables.
//    005   24.11.23 Sean Flook                  Moved Box and Stack to @mui/system.
//    006   08.12.23 Sean Flook                  Migrated DataGrid to v6.
//    007   02.01.24 Sean Flook                  Changed console.log to console.error for error messages.
//    008   05.01.24 Sean Flook                  Use CSS shortcuts.
//    009   10.01.24 Sean Flook                  Fix warnings.
//    010   22.03.24 Sean Flook            GLB12 Changed to use dataFormStyle so height can be correctly set.
//    011   27.03.24 Sean Flook                  Further changes to fix warnings.
//    012   20.05.24 Sean Flook        IMANN-445 Display API errors.
//    013   04.06.24 Sean Flook        IMANN-445 Show save data.
//    014   04.06.24 Sean Flook        IMANN-445 Only close the edit dialog if the save was successful.
//    015   19.06.24 Sean Flook        IMANN-629 Changes to code so that current user is remembered and a 401 error displays the login dialog.
//    016   10.09.24 Sean Flook        IMANN-980 Only write to the console if the user has the showMessages right.
//endregion Version 1.0.0.0
//region Version 1.0.5.0
//    017   27.01.25 Sean Flook       IMANN-1077 Upgraded MUI to v6.
//    018   30.01.25 Sean Flook       IMANN-1673 Changes required for new user settings API.
//endregion Version 1.0.5.0
//region Version 1.0.6.0
//    019   23.06.25 Sean Flook       IMANN-1838 Hide the Add and Delete layer buttons/options.
//endregion Version 1.0.6.0
//region Version 1.0.7.0
//    020   13.06.25 Sean Flook       IMANN-1812 Moved controls from @mui/system to @mui/material.
//    021   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    022   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI X to v8.
//    023   27.10.25 Sean Flook       IMANN-2110 Set getRowHeight to Auto in the grid so that text wraps correctly.
//    024   29.10.25 Sean Flook       IMANN-1812 Changed to split the map layers into base, reference and customer.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useState } from "react";

import { Grid, Typography, Stack } from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { TreeItemStyle } from "../utils/ADSStyles";
import MapLayersDataForm from "../forms/MapLayersDataForm";

import { useTheme } from "@mui/material/styles";

function MapLayersTab(props) {
  const theme = useTheme();

  const [selectedNode, setSelectedNode] = useState("BASE_MAPS");

  /**
   * Event to handle when a node is selected.
   *
   * @param {object} event The event object.
   * @param {number} nodeId The node id.
   */
  const handleNodeSelect = (event, nodeId) => {
    event.stopPropagation();
    setSelectedNode(nodeId);
  };

  return (
    <Grid container justifyContent="flex-start" spacing={0}>
      <Grid size={12}>
        <Grid container spacing={0} justifyContent="flex-start">
          <Grid size={2}>
            <Stack direction="column" spacing={2}>
              <Typography sx={{ fontSize: 24, flexGrow: 1, pl: theme.spacing(3.5) }}>Map layers</Typography>
              <SimpleTreeView
                aria-label="map layers navigator"
                defaultExpandedItems={["DEFAULT_SYMBOLOGY"]}
                defaultSelectedItems={"BASE_MAPS"}
                selectedItems={selectedNode}
                slots={{ expandIcon: ChevronRightIcon, collapseIcon: ExpandMoreIcon }}
                sx={{ overflowY: "auto" }}
                onSelectedItemsChange={handleNodeSelect}
              >
                <TreeItem
                  itemId="BASE_MAPS"
                  label={
                    <Typography variant="body2" sx={{ fontWeight: "inherit", flexGrow: 1 }}>
                      Basemap gallery
                    </Typography>
                  }
                  sx={TreeItemStyle(selectedNode === "BASE_MAPS")}
                />
                <TreeItem
                  itemId="REFERENCE_MAPS"
                  label={
                    <Typography variant="body2" sx={{ fontWeight: "inherit", flexGrow: 1 }}>
                      Reference maps
                    </Typography>
                  }
                  sx={TreeItemStyle(selectedNode === "REFERENCE_MAPS")}
                />
                <TreeItem
                  itemId="CUSTOM_MAPS"
                  label={
                    <Typography variant="body2" sx={{ fontWeight: "inherit", flexGrow: 1 }}>
                      Custom maps
                    </Typography>
                  }
                  sx={TreeItemStyle(selectedNode === "CUSTOM_MAPS")}
                />
              </SimpleTreeView>
            </Stack>
          </Grid>
          <Grid size={10}>
            <MapLayersDataForm nodeId={selectedNode} />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default MapLayersTab;
